import i18next from 'i18next';

import { TimeOffAllocationFormat, TimeOffTypeCode } from '@/modules/common/types/time-off-type';
import { formatShortDate } from '@/utils/date-formatters';

import { TimeOffBalanceSummaryItemResponse } from '../types/dto/time-off-balance.dto';
import { TimeOffBalance, TimeOffBalanceInfo } from '../types/time-off-balance';

const mapTimeOffTypeCode = (code: string | null): TimeOffTypeCode | null => {
  if (!code) return null;
  if (Object.values(TimeOffTypeCode).includes(code as TimeOffTypeCode)) {
    return code as TimeOffTypeCode;
  }
  return null;
};

const formatRenewalDate = (
  renewalDay: number,
  renewalMonth: number | null,
  allocationFormat: TimeOffAllocationFormat
): string => {
  if (allocationFormat === TimeOffAllocationFormat.Monthly) {
    return i18next.t('authenticated.timeOff:balances.renewal.monthly', { day: renewalDay });
  }

  const monthDate = new Date(2000, (renewalMonth ?? 1) - 1, renewalDay);
  const formattedDate = formatShortDate(monthDate);
  return i18next.t('authenticated.timeOff:balances.renewal.yearly', { date: formattedDate });
};

export const mapTimeOffBalanceFromDTO = (
  response: TimeOffBalanceSummaryItemResponse
): TimeOffBalance => {
  const policy = response.policy;
  const allocationFormatString = policy?.allocationFormat ?? 'YEARLY';
  const allocationFormat =
    allocationFormatString === 'MONTHLY'
      ? TimeOffAllocationFormat.Monthly
      : TimeOffAllocationFormat.Yearly;
  const renewalDay = policy?.renewalDay ?? 1;
  const renewalMonth = policy?.renewalMonth ?? 1;

  const info: TimeOffBalanceInfo = {
    allocationFormat,
    dateOfRenewalLabel: formatRenewalDate(renewalDay, renewalMonth, allocationFormat),
    carryoverDays: policy?.allowCarryover ? policy.carryoverDays : null,
    negativeBalanceDays: policy?.allowNegativeBalance ? policy.negativeBalanceLimit : null,
    numberOfDays: policy?.numberOfDays ?? 0,
    renewalDay,
    renewalMonth,
    allowCarryover: policy?.allowCarryover ?? false,
    allowNegativeBalance: policy?.allowNegativeBalance ?? false,
    negativeBalanceLimit: policy?.negativeBalanceLimit ?? null,
    maximumBalanceLimit: policy?.maximumBalanceLimit ?? null,
    isBillable: policy?.isBillable ?? false,
    effectiveDate: policy?.effectiveDate ?? '',
  };

  return {
    type: {
      id: response.timeOffTypeId,
      code: mapTimeOffTypeCode(response.timeOffTypeCode) ?? TimeOffTypeCode.PAID_VACATION,
      displayName: response.timeOffTypeName,
      format: allocationFormat,
      allocationDays: policy?.numberOfDays ?? 0,
      dateOfRenewal: { month: renewalMonth, day: renewalDay },
      isApprovalRequired: policy?.approvalRequired ?? false,
    },
    balanceDays: response.balance,
    availableDays: response.available,
    bookedDays: response.booked,
    requestedDays: response.requested > 0 ? response.requested : null,
    info,
    policy: policy
      ? {
          allowNegativeBalance: policy.allowNegativeBalance,
          negativeBalanceLimit: policy.negativeBalanceLimit,
          requiresDocument: policy.requiresDocument,
          approvalRequired: policy.approvalRequired,
          allowHalfDay: policy.allowHalfDay,
          allowQuarterDay: policy.allowQuarterDay,
          allowHourly: policy.allowHourly,
        }
      : null,
  };
};
